import { useState } from "react";
import { Alert, App, Button, Modal, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { PlanImportPreviewDto, PlanImportRow } from "@audit-plan/shared";
import { ApiError, download } from "../../api/client";
import { usePlanImportConfirm, usePlanImportPreview } from "../../api/plan";
import { Pill, fmtWhen } from "../pills";
import { ResponsiveList } from "../ResponsiveList";
import { useModalProps } from "../useModalProps";

export function ImportPlanModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const preview = usePlanImportPreview();
  const confirm = usePlanImportConfirm();
  const modalProps = useModalProps(900);
  const [data, setData] = useState<PlanImportPreviewDto | null>(null);

  const close = () => {
    setData(null);
    preview.reset();
    onClose();
  };
  const pick = async (file: File) => {
    try {
      setData(await preview.mutateAsync(file));
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
    return false;
  };
  const valid = (data?.rows ?? []).filter((r) => !r.why.length);
  const go = async () => {
    try {
      await confirm.mutateAsync(valid.map((r) => ({ supplierId: r.supplierId!, factoryId: r.factoryId, type: r.type, date: r.date!, dateTo: r.dateTo, auditorId: r.auditorId! })));
      void message.success(`${valid.length} ${t("a_auditsImported")}`);
      close();
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      {...modalProps}
      title={t("importXlsx")}
      footer={[
        <Button key="tpl" onClick={() => download("/plan/template.xlsx")}>
          {t("template")}
        </Button>,
        <Button key="c" onClick={close}>
          {t("cancel")}
        </Button>,
        <Button key="ok" type="primary" disabled={!valid.length} loading={confirm.isPending} onClick={go}>
          {t("a_import")} {valid.length ? `(${valid.length})` : ""}
        </Button>,
      ]}
    >
      <p style={{ opacity: 0.75 }}>{t("a_importHelp")}</p>
      <Upload.Dragger accept=".xlsx,.csv" maxCount={1} showUploadList={false} beforeUpload={pick} disabled={preview.isPending}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p>{preview.isPending ? t("a_reading") : t("a_importPick")}</p>
        <p style={{ opacity: 0.6 }}>.xlsx · .csv</p>
      </Upload.Dragger>
      {data && (
        <div style={{ marginTop: 16 }}>
          <Alert
            style={{ marginBottom: 10 }}
            type={data.discarded ? "warning" : "success"}
            showIcon
            message={`${data.valid} ${t("a_validRows")} · ${data.discarded} ${t("a_discardedRows")}`}
          />
          <ResponsiveList<PlanImportRow>
            size="small"
            rowKey="row"
            dataSource={data.rows}
            pageSize={10}
            emptyText={t("none")}
            card={{
              code: (r) => `#${r.row}`,
              title: (r) => r.supplierName ?? <span style={{ opacity: 0.6 }}>{r.raw.supplier ?? r.raw.sap ?? r.raw.nm ?? "—"}</span>,
              subtitle: (r) => `${r.factoryName && r.factoryName !== r.supplierName ? `${r.factoryName} · ` : ""}${t(`t_${r.type}`)} · ${fmtWhen({ plannedDate: r.date, plannedDateTo: r.dateTo })}`,
              tags: (r) => (r.why.length ? <Pill tone="bad">{r.why.join(", ")}</Pill> : <Pill tone="ok">OK</Pill>),
              fields: (r) => [[t("auditor"), r.auditorName ?? <span style={{ opacity: 0.6 }}>{r.raw.auditor ?? r.raw.aud ?? "—"}</span>]],
            }}
            columns={[
              { title: "#", dataIndex: "row", width: 50 },
              { title: t("supplier"), render: (_, r) => r.supplierName ?? <span style={{ opacity: 0.6 }}>{r.raw.supplier ?? r.raw.sap ?? r.raw.nm ?? "—"}</span> },
              { title: t("f_factory"), render: (_, r) => r.factoryName ?? <span style={{ opacity: 0.6 }}>{r.raw.stabilimento || "—"}</span> },
              { title: t("auditType"), dataIndex: "type", render: (v: string) => t(`t_${v}`) },
              { title: t("date"), dataIndex: "date", render: (v: string | null, r: PlanImportRow) => fmtWhen({ plannedDate: v, plannedDateTo: r.dateTo }) },
              { title: t("auditor"), render: (_, r) => r.auditorName ?? <span style={{ opacity: 0.6 }}>{r.raw.auditor ?? r.raw.aud ?? "—"}</span> },
              {
                title: t("status"),
                render: (_, r) => (r.why.length ? <Pill tone="bad">{r.why.join(", ")}</Pill> : <Pill tone="ok">OK</Pill>),
              },
            ]}
          />
        </div>
      )}
    </Modal>
  );
}
